import type { TFunction } from 'i18next';
import { forwardRef, Fragment, useCallback, useEffect, useMemo } from 'react';
import { useMergeRefs } from '@floating-ui/react';
import { faArrowUpFromBracket } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Stack, Text } from '@sixui/ui';
import { colorSchemeTokens } from '@sixui/ui/themes/base/colorScheme.stylex';
import stylex from '@stylexjs/stylex';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import type { IFileAssetProps } from '~/components/molecules/FileAsset';
import type { IContainerProps } from '~/components/utils/Container';
import type { IAny, IMakeRequired, IMaybeAsync } from '~/helpers/types';
import { DropZone } from '~/components/atoms/DropZone';
import { FileAsset } from '~/components/molecules/FileAsset';
import { getIconFromMimeType } from '~/helpers/getIconFromMimeType';

export type IFileDropZoneFileInfo = {
  key: string;
  id?: string;
  thumbUrl?: string;
  downloadUrl?: string;
  mimeType: string;
  name: string;
  size: number;
  file?: File;
  loading?: boolean;
  progress?: number;
  errorText?: string;
  canRetry?: boolean;
  supportingText?: string;
  abortController?: AbortController;
};

export type IFileDropZonePartialFileInfo = IMakeRequired<
  Partial<IFileDropZoneFileInfo>,
  'key'
>;

export type IFileDropZoneProps = IContainerProps & {
  id?: string;
  supportingText?: string;
  errorText?: React.ReactNode;

  /**
   * Maximum accepted number of files
   */
  maxFiles?: number;

  files?: Array<IFileDropZonePartialFileInfo>;

  /**
   * Maximum file size (in bytes)
   */
  maxSize?: number;

  /**
   * Accepted file types
   *
   * Checkout https://developer.mozilla.org/en-US/docs/Web/API/window/showOpenFilePicker types option for more information. Keep in mind that mime type determination is not reliable across platforms. CSV files, for example, are reported as text/plain under macOS but as application/vnd.ms-excel under Windows. In some cases there might not be a mime type set at all (https://github.com/react-dropzone/react-dropzone/issues/276).
   */
  accept?: Record<string, Array<string>>;

  capture?: 'user' | 'environment';
  extraActions?: React.ReactNode;
  onAcceptedDrop?: (
    acceptedFilesInfo: Array<IFileDropZoneFileInfo>,
  ) => IMaybeAsync<IAny>;
  onRejectedDrop?: (
    rejectedFilesInfo: Array<IFileDropZoneFileInfo>,
  ) => IMaybeAsync<IAny>;
  onDelete?: (fileInfo: IFileDropZonePartialFileInfo) => IMaybeAsync<IAny>;
  onRetry?: (fileInfo: IFileDropZoneFileInfo) => IMaybeAsync<IAny>;
  disabled?: boolean;
  renderFileAsset?: (props: IFileAssetProps) => React.ReactNode;
};

const styles = stylex.create({
  host: {
    flexGrow: 1,
    borderRadius: '10px',
  },
  dropZoneContainer: {
    borderRadius: 'inherit',
    flexGrow: 1,
  },
  inputContainer: {
    display: 'flex',
    borderRadius: 'inherit',
    flexGrow: 1,
  },
  dropZone: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    borderRadius: 'inherit',
  },
  placeholder: {
    gap: '1rem',
    height: '100%',
  },
  dropActions: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-evenly',
  },
  files: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    gap: 8,
  },
  supportingText: {
    display: 'flex',
    flexDirection: 'row',
    color: colorSchemeTokens.onSurfaceVariant,
  },
  supportingTextLeft: {
    flexGrow: 1,
  },
  supportingTextRight: {
    flexGrow: 0,
  },
  supportingText$error: {
    color: colorSchemeTokens.error,
  },
  fileInput: {
    display: 'initial',
    opacity: '0',
    position: 'absolute',
    zIndex: -1,
  },
  buttonPlaceholder: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    height: 40,
    paddingLeft: 16,
    paddingRight: 24,
  },
  buttonIconPlaceholder: {
    fontSize: 18,
    inlineSize: 18,
    blockSize: 18,
  },
  label: {
    whiteSpace: 'pre-wrap',
  },
});

const getHumanReadableError = (
  code: string,
  t: TFunction<'translation', undefined>,
): string => {
  switch (code) {
    case 'file-invalid-type':
      return t('FileDropZone.error.file-invalid-type');
    case 'file-too-large':
      return t('FileDropZone.error.file-too-large');
    case 'too-many-files':
      return t('FileDropZone.error.too-many-files');
    default:
      return t('FileDropZone.error.unknown');
  }
};

export const FileDropZone = forwardRef<HTMLInputElement, IFileDropZoneProps>(
  function FileDropZone(props, forwardedRef) {
    const {
      sx,
      supportingText,
      errorText,
      files,
      maxFiles,
      maxSize,
      accept,
      capture,
      extraActions,
      onAcceptedDrop,
      onRejectedDrop,
      onDelete,
      onRetry,
      disabled: disabledProp,
      renderFileAsset = (props: IFileAssetProps) => <FileAsset {...props} />,
      ...other
    } = props;
    const { t } = useTranslation();
    const maxUploadableFiles =
      maxFiles !== undefined
        ? Math.max(maxFiles - (files?.length ?? 0), 0)
        : undefined;
    const multiple = !maxFiles || maxFiles > 1;
    const canAddMore = !maxFiles || !files || files.length < maxFiles;
    const canReplace = maxFiles === 1;
    const disabled = disabledProp || (!canAddMore && !canReplace);
    const hasError = !!errorText;

    const {
      getRootProps,
      getInputProps,
      isDragActive,
      open: browse,
      inputRef,
    } = useDropzone({
      noClick: true,
      noKeyboard: true,
      accept,
      maxSize,
      maxFiles: maxUploadableFiles,
      multiple,
      onError: (error) => {
        // TODO: handle error

        // eslint-disable-next-line no-console
        console.error(error);
      },
      onDrop: disabled
        ? undefined
        : (acceptedFiles, rejectedFiles) => {
            if (rejectedFiles.length > 0) {
              void onRejectedDrop?.(
                rejectedFiles.map((rejectedFile) => ({
                  key: String(Math.random()),
                  name: rejectedFile.file.name,
                  size: rejectedFile.file.size,
                  mimeType: rejectedFile.file.type,
                  file: rejectedFile.file,
                  errorText: rejectedFile.errors
                    .map(({ code }) => getHumanReadableError(code, t))
                    .join(' / '),
                })),
              );
            }

            if (acceptedFiles.length > 0) {
              void onAcceptedDrop?.(
                acceptedFiles.map((acceptedFile) => ({
                  key: String(Math.random()),
                  thumbUrl: acceptedFile.type.startsWith('image/')
                    ? URL.createObjectURL(acceptedFile)
                    : undefined,
                  name: acceptedFile.name,
                  size: acceptedFile.size,
                  mimeType: acceptedFile.type,
                  file: acceptedFile,
                  abortController: new AbortController(),
                })),
              );
            }
          },
    });

    useEffect(() => {
      // Make sure to revoke the data URIs to avoid memory leaks, will run on
      // unmount.
      return () =>
        files?.forEach((file) =>
          file.file && file.thumbUrl
            ? URL.revokeObjectURL(file.thumbUrl)
            : undefined,
        );
    }, [files]);

    const footer = useMemo(():
      | { message: string; counter: string }
      | undefined => {
      const count = files?.length;
      if (!maxFiles || !count || maxFiles <= 1 || count < maxFiles) {
        return undefined;
      }

      return {
        message: t('FileDropZone.error.max-files'),
        counter: `${count} / ${maxFiles}`,
      };
    }, [files, maxFiles, t]);

    const hasFooter = !!errorText || !!footer || !!supportingText;

    const renderFileAssets = useCallback(
      () => (
        <div {...stylex.props(styles.files)}>
          {files?.map(
            (file, fileIndex): React.ReactNode => (
              <Fragment key={fileIndex}>
                {renderFileAsset({
                  icon: (
                    <FontAwesomeIcon
                      icon={getIconFromMimeType(file.mimeType)}
                    />
                  ),
                  thumbUrl: file.thumbUrl,
                  downloadUrl: file.downloadUrl,
                  name: file.name,
                  size: file.size,
                  loading: file.loading,
                  supportingText: file.supportingText,
                  progress: file.progress,
                  errorText: file.errorText,
                  onReplace: canReplace ? browse : undefined,
                  onDelete: onDelete ? () => void onDelete(file) : undefined,
                  onRetry:
                    onRetry &&
                    file.canRetry &&
                    file.name &&
                    file.size &&
                    file.mimeType &&
                    true
                      ? () => void onRetry(file as IFileDropZoneFileInfo)
                      : undefined,
                  hideMetadata: true,
                })}
              </Fragment>
            ),
          )}
        </div>
      ),
      [files, canReplace, onDelete, onRetry, renderFileAsset, browse],
    );

    const handleRef = useMergeRefs([forwardedRef, inputRef]);

    return (
      <Stack gap={4} sx={[styles.host, sx]}>
        <Stack gap={2} sx={styles.dropZoneContainer}>
          <input
            {...getInputProps()}
            {...other}
            ref={handleRef}
            capture={capture}
          />
          {!multiple && files?.length ? (
            renderFileAssets()
          ) : (
            <div {...getRootProps()} {...stylex.props(styles.inputContainer)}>
              <DropZone
                sx={styles.dropZone}
                active={isDragActive && !disabled}
                onClick={extraActions ? undefined : browse}
                disabled={disabled}
              >
                <DropZone.Placeholder sx={styles.placeholder}>
                  <div {...stylex.props(styles.label)}>
                    {multiple
                      ? t('FileDropZone.drag-multiple')
                      : t('FileDropZone.drag-single')}
                  </div>

                  <div {...stylex.props(styles.dropActions)}>
                    {extraActions ? (
                      <>
                        <Button
                          variant="text"
                          disabled={disabled}
                          onClick={browse}
                          icon={<FontAwesomeIcon icon={faArrowUpFromBracket} />}
                        >
                          {t('FileDropZone.browse')}
                        </Button>
                        {extraActions}
                      </>
                    ) : (
                      <div {...stylex.props(styles.buttonPlaceholder)}>
                        <div {...stylex.props(styles.buttonIconPlaceholder)}>
                          <FontAwesomeIcon icon={faArrowUpFromBracket} />
                        </div>
                        <div>{t('FileDropZone.browse')}</div>
                      </div>
                    )}
                  </div>
                </DropZone.Placeholder>
              </DropZone>
            </div>
          )}

          {hasFooter ? (
            <Text
              sx={[
                styles.supportingText,
                hasError && styles.supportingText$error,
              ]}
              variant="body"
              size="sm"
              as="div"
            >
              <div {...stylex.props(styles.supportingTextLeft)}>
                {errorText && <div>{errorText}</div>}
                {footer?.message ?? supportingText}
              </div>
              {footer?.counter ? (
                <div {...stylex.props(styles.supportingTextRight)}>
                  {footer.counter}
                </div>
              ) : null}
            </Text>
          ) : null}
        </Stack>

        {multiple && files?.length ? renderFileAssets() : null}
      </Stack>
    );
  },
);
